import React, { createContext, useContext, useState, useEffect } from "react";
import { decodeTokenService } from "../services/decodeTokenService";
import { Language } from "../utils/dictionary";
import { Country } from "../const/countryList";
import {
  SupportTestSession,
  clearSupportSession,
  exchangeSupportLaunchCode,
  getCurrentSupportSession,
  readSupportSession,
  storeDelegatedSupportAccessToken,
  storeSupportSession,
} from "../services/supportTestSessionService";
import {
  getAccessToken,
  getDelegatedAccessToken,
  migrateLegacyDelegatedAccessToken,
} from "../services/authStorage";

interface AuthContextType {
  setLanguage: (language: Language) => void;
  setCountry: (country: Country) => void;
  setTimezone: (timezone: string) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (access_token: string, refresh_token: string) => void;
  logout: () => void;
  refreshAuth: () => void;
  isUserFake: boolean;
  language: Language;
  country: Country;
  timezone: string;
  credits: number;
  setCredits: (credits:number) => void;
  displayName: string;
  setDisplayName: (name: string) => void;
  supportSession: SupportTestSession | null;
}

let pendingSupportExchange: ReturnType<typeof exchangeSupportLaunchCode> | null = null;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getInitialLanguage = (): Language => {
  const savedLanguage = localStorage.getItem("user_language");
  if (savedLanguage && Object.values(Language).includes(savedLanguage as Language)) {
    return savedLanguage as Language;
  }
  return Language.SPANISH;
};

const getInitialCountry = (): Country => {
  const savedCountry = localStorage.getItem("user_country");
  if (savedCountry && Object.values(Country).includes(savedCountry as Country)) {
    return savedCountry as Country;
  }
  return Country.COLOMBIA;
};

const getInitialTimezone = (): string => {
  const saved = localStorage.getItem("user_timezone");
  if (saved) return saved;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/Bogota";
  }
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [isUserFake, setIsUserFake] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Prevent premature redirects
  const [language, _setLanguage] = useState<Language>(getInitialLanguage);
  const [country, setCountry] = useState<Country>(getInitialCountry);
  const [timezone, setTimezone] = useState<string>(getInitialTimezone);
  const [credits, setCredits] = useState<number>(0);
  const [displayName, _setDisplayName] = useState<string>(
    () => localStorage.getItem("user_display_name") || ""
  );
  const [supportSession, setSupportSession] = useState<SupportTestSession | null>(
    () => readSupportSession()
  );
  const [sessionError, setSessionError] = useState<string | null>(null);

  const setDisplayName = (name: string) => {
    localStorage.setItem("user_display_name", name);
    _setDisplayName(name);
  };

  const setLanguage = (lang: Language) => {
    localStorage.setItem("user_language", lang);
    _setLanguage(lang);
  };

  const setTimezonePersist = (tz: string) => {
    localStorage.setItem("user_timezone", tz);
    setTimezone(tz);
  };

  const checkAuthentication = () => {
    setIsLoading(true);
    try {
      const access_token = getAccessToken();
      const refresh_token = localStorage.getItem("refresh_token");

      if (access_token || refresh_token) {
        const user_id = decodeTokenService.getUserIdFromToken()
        
        if (user_id === import.meta.env.VITE_REACT_APP_USER_ID_FAKE) {
          setIsUserFake(true);
        }
        setIsAuthenticated(true);
      
      } else {
        setIsAuthenticated(false);
      }

    } catch (error) {
      console.error("Error validating authentication:", error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (access_token: string, refresh_token: string) => {
    clearSupportSession();
    setSupportSession(null);
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);
    setIsAuthenticated(true);
    const isMobile = window.innerWidth < 768;
    const redirectPath = isMobile ? "/settings" : "/home";
    window.location.href = redirectPath;
  };

  const logout = () => {
    const delegated = Boolean(getDelegatedAccessToken());
    if (!delegated) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
    clearSupportSession();
    setSupportSession(null);
    // localStorage.removeItem("user_language");
    setIsAuthenticated(false);
    // setLanguageState(Language.SPANISH);
  };

  const refreshAuth = () => {
    checkAuthentication();
  };

  useEffect(() => {
    let mounted = true;
    const searchParams = new URLSearchParams(window.location.search);
    const supportCode = searchParams.get("support_code");
    searchParams.delete("support_code");
    searchParams.delete("token");
    const cleanedUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams}` : ""}${window.location.hash}`;
    window.history.replaceState({}, "", cleanedUrl);

    const initialize = async () => {
      try {
        const migratedLegacySession = migrateLegacyDelegatedAccessToken();
        let validatedSupportSession: SupportTestSession | null = null;
        if (supportCode) {
          pendingSupportExchange ||= exchangeSupportLaunchCode(supportCode);
        }
        if (pendingSupportExchange) {
          const exchanged = await pendingSupportExchange;
          storeDelegatedSupportAccessToken(exchanged.accessToken);
          storeSupportSession(exchanged.session);
          validatedSupportSession = exchanged.session;
          pendingSupportExchange = null;
        }
        if (!validatedSupportSession && (migratedLegacySession || readSupportSession())) {
          validatedSupportSession = await getCurrentSupportSession();
          storeSupportSession(validatedSupportSession);
        }
        if (mounted && validatedSupportSession) {
          setSupportSession(validatedSupportSession);
        }
        if (mounted) checkAuthentication();
      } catch (error) {
        console.error("No se pudo abrir la sesión delegada:", error);
        pendingSupportExchange = null;
        clearSupportSession();
        if (mounted) {
          setSupportSession(null);
          setSessionError("El enlace de soporte ya fue usado, venció o la sesión fue revocada.");
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      }
    };

    void initialize();
    return () => {
      mounted = false;
    };
  }, []);

  if (isLoading) {
    return <div>Loading Authentication...</div>;
  }

  if (sessionError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md rounded-2xl border border-amber-200 bg-white p-8 text-center shadow-lg">
          <h1 className="mb-3 text-xl font-bold text-slate-900">No se pudo abrir la sesión de soporte</h1>
          <p className="mb-6 text-sm leading-6 text-slate-600">{sessionError}</p>
          <a className="font-semibold text-indigo-600" href="/login">Volver al inicio de sesión</a>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshAuth,
        isUserFake,
        language,
        setLanguage,
        country,
        setCountry,
        timezone,
        setTimezone: setTimezonePersist,
        credits,
        setCredits,
        displayName,
        setDisplayName,
        supportSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
